import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, Output, signal, OnInit } from '@angular/core';
import { MenuItem } from 'primeng/api';
import { TranslationService } from '../../../../common/service/translate.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AuthService } from '../../../auth/auth.service';
import { MenuModule } from 'primeng/menu';
import { environment } from '../../../../environment/environment';
import { Constants } from '../../../../common/helper/Constants';
import { ButtonModule } from 'primeng/button';

import { Router, RouterLink } from '@angular/router';
import { ShoppingCartMenuComponent } from "./shopping-cart-menu/shopping-cart-menu.component";
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { FormsModule } from '@angular/forms';
import { Role } from '../../../auth/role.enum';
import { PermissionDirective } from '../../../auth/permission.directive';
import { CustomerService } from '../../../../common/service/customer.service';
import { PlatformService } from '../../../../common/service/platform.service';
import { UserPreferenceService } from '../../../../common/service/user-preference.service';
import { UserService } from '../../../../common/service/user.service';
@Component({
  selector: 'gate-menu',
  standalone: true,
  imports: [MenuModule, TranslatePipe, ButtonModule, RouterLink, ShoppingCartMenuComponent, ToggleSwitchModule, FormsModule, PermissionDirective],
  templateUrl: './menu.component.html',
  styleUrl: './menu.component.scss',
  // changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MenuComponent implements OnInit {
  // Injecting necessary services
  private readonly translationService = inject(TranslationService);
  private readonly translateService = inject(TranslateService);
  private readonly authService = inject(AuthService);
  private readonly customerService = inject(CustomerService);
  private readonly router = inject(Router);
  private readonly platformService = inject(PlatformService);
  private readonly userPreferenceService = inject(UserPreferenceService);
  private readonly userService = inject(UserService);
  public role = Role;
  // Menu items for languages and settings
  settings!: MenuItem[];
  searchQuery = signal('');
  public readonly locale = this.translationService.currentLanguage;
  public isLightMode = signal(true);
  public isHeroVisible = signal(true);
  public route = Constants.routes;
  private readonly apiDocumentationUrl = environment.partnerApiDocumentationUrl;
  @Input() mobileFullView = false;
  @Output() openSettingsFullView = new EventEmitter<void>();
  @Output() openCartFullView = new EventEmitter<void>();


  /**
   * Initializes the component and sets up the menu items.
   */
  ngOnInit() {
    this.applyPreferenceState();
    this.userPreferenceService.initialize().then(() => {
      this.applyPreferenceState();
      this.platformService.heroToggleEvent.emit(this.isHeroVisible());
      this.setMenuItems();
    });
    this.setMenuItems();
    this.translateService.onLangChange.subscribe(() => {
      this.setMenuItems();
    });
    this.platformService.heroToggleEvent.subscribe((value) => {
      this.isHeroVisible.set(value);
      this.setMenuItems();
    });
    this.userService.reloadPage.subscribe(() => {
      console.log('Reloading page due to user service event');
      this.setMenuItems();
    });
    // this.userService.impersonationStatus.subscribe(() => {
    //   this.setMenuItems();
    // });
  }

  /**
   * Initializes the menu items for languages and settings.
   */

private setMenuItems() {
    const profile = 'menu.profile';
    const darkLightMode = 'menu.darkLightMode';
    const masterData = 'menu.masterData';
    const feedback = 'menu.feedback';
    const logOut = 'menu.logOut';
    const language = 'menu.language.lang';
    const apiDocumentation = 'menu.apiDocumentation';
    const heroAreaAction = this.isHeroVisible() ? 'menu.hideHeroArea' : 'menu.showHeroArea';
    this.translateService.get([language, profile, masterData, feedback, logOut, darkLightMode, heroAreaAction, apiDocumentation]).subscribe((translated) => {
      this.settings = [
        {
          label: translated[profile],
          target: '_blank',
          url: environment.UserProfileUpdateUrl,
          testid: 'profile-link'
        },
        {
          label: translated[masterData],
          routerLink: Constants.routes.customerMasterData(),
          testid: 'master-data-link'
        },
        {
          label: translated[feedback],
          command: () => this.router.navigate([this.route.feedback()]),
          testid: 'feedback-link'
        },
         ...(this.hasPartnerApiReadPermission() ? [{
                label: translated[apiDocumentation],
                target: '_blank',
                url: this.apiDocumentationUrl,
                testid: 'api-documentation-link'
            }] : []),
        {
          label: translated[darkLightMode],
          isThemeColorToggle: true,
          isToggleOption: true,
          testid: 'theme-toggle'
        },
        {
          label: translated[language],
          isLangToggle: true,
          isToggleOption: true,
          testid: 'language-toggle'
        },
        {
          label: translated[heroAreaAction],
          isHeroToggle: true,
          isToggleOption: true,
          testid: 'hero-toggle'
        },
        
        {
          label: translated[logOut],
          icon: 'pi pi-sign-out',
          command: () => this.logOut(),
          testid: 'logout-button'
        },
      ];
    });
  }

  /**
   * Handles the logout action.
   */
  logOut(): void {
    this.customerService.clearCustomerCache();
    this.authService.logout();
  }

  /**
   * Changes the language of the application.
   * @param lang The language code to change to.
   */
  changeLanguage(lang: string) {
    this.translationService.setLanguage(lang);
    if (lang === 'en-US' || lang === 'de-DE') {
      this.userPreferenceService.setLanguage(lang);
    }
  }

  toggleLanguage(){
    const newLang = this.locale() == 'de-DE' ? 'en-US' : 'de-DE';
    this.changeLanguage(newLang);
  }

  toggleDarkMode() {
    const isDarkMode = !this.userPreferenceService.isDarkMode();
    this.isLightMode.set(!isDarkMode);
    this.userPreferenceService.setDarkMode(isDarkMode);
  }

  toggleHeroVisibility(nextValue: boolean) {
    this.isHeroVisible.set(nextValue);
    this.platformService.heroToggleEvent.emit(nextValue);
    this.userPreferenceService.setShowHeroSection(nextValue);
  }

  private applyPreferenceState(): void {
    this.isLightMode.set(!this.userPreferenceService.isDarkMode());
    this.isHeroVisible.set(this.userPreferenceService.showHeroSection());
  }

  private hasPartnerApiReadPermission(): boolean {
    return this.userService.getRoles('role').includes(Role.GatePartnerApiRead);
  }
  openSettingsMenu(event: Event, settingMenu: any) {
    if (this.mobileFullView) {
      event.preventDefault();
      event.stopPropagation();
      this.openSettingsFullView.emit();
      return;
    }

    settingMenu.toggle(event);
  }

}
